import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { MultiSelectAutocomplete } from '../components/MultiSelectAutocomplete';
import { DateRangePicker }         from '../components/DateRangePicker';
import { EmployeeMultiSelect }     from '../components/EmployeeMultiSelect';

function StatusBadge({ estado, num_fichajes }) {
  const styles = {
    'Correcto':                       { bg: '#22c55e', color: '#052e16', label: 'Correcto' },
    'Fichaje cerrado automáticamente': { bg: '#f97316', color: '#1c0700', label: 'Turno sin salida' },
    'Falta fichaje salida':            { bg: '#ef4444', color: '#fff',    label: 'Falta salida' },
  };
  const s = styles[estado] || { bg: '#64748b', color: '#fff', label: estado };
  // Añade subtipo A si sólo hay 1 fichaje (ayuda a distinguir del subtipo B/C).
  const subtipoA = estado === 'Fichaje cerrado automáticamente' && (num_fichajes ?? 1) === 1;
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <span style={{
        background: s.bg, color: s.color,
        borderRadius: '6px', padding: '2px 8px',
        fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap'
      }}>{s.label}</span>
      {subtipoA && (
        <span
          title="Categoría A · La persona sólo tiene 1 fichaje en todo el día"
          style={{
            background: 'rgba(251,191,36,0.15)', color: '#fbbf24',
            border: '1px solid rgba(251,191,36,0.4)',
            borderRadius: '4px', padding: '1px 5px',
            fontSize: '10px', fontWeight: 700, fontFamily: 'IBM Plex Mono',
            cursor: 'help',
          }}
        >A</span>
      )}
    </span>
  );
}

function ShiftBadge({ shift }) {
  const styles = { 'Mañana': '#3b82f6', 'Tarde': '#f97316', 'Noche': '#a78bfa' };
  const color = styles[shift] || '#64748b';
  return (
    <span style={{ color, background: `${color}1a`, borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
      {shift}
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function formatDate(str) {
  if (!str) return '-';
  try { return new Date(str + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return str; }
}

export default function FichajesPage() {
  const { apiFetch, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  // Multi-select: arrays of strings
  const [turnos,  setTurnos]    = useState([]);
  const [estados, setEstados]   = useState([]);
  const [centrosSel, setCentrosSel] = useState([]);
  const [empleadosSel, setEmpleadosSel] = useState([]); // [{id, label, centro}]
  const [fechaIni, setFechaIni] = useState(yesterdayStr);
  const [fechaFin, setFechaFin] = useState(yesterdayStr);
  const [facets, setFacets]     = useState({ centros: [], turnos: [], estados: [] });
  const [perPage]               = useState(50);

  // Stale-data banner state (when last sync failed and "yesterday" is empty)
  const [staleInfo, setStaleInfo] = useState(null); // { last_available_date, yesterday }
  // User-RLS empty banner: user's centers have NO data on the currently selected date
  const [userEmptyInfo, setUserEmptyInfo] = useState(null); // { user_last_available_date, user_kpis_on_last_day }
  // Cache of last-available-date response (so we don't fetch it on every load)
  const [lastDateInfo, setLastDateInfo] = useState(null);

  // Rectification state
  const [showRectifyDialog, setShowRectifyDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rectifyField, setRectifyField] = useState('entrada');
  // Inline shift override (corrección manual de Mañana/Tarde/Noche)
  const [shiftEditRow, setShiftEditRow] = useState(null);
  const [shiftEditValue, setShiftEditValue] = useState('');

  // UTE RM VAO export (visible solo para admin o usuarios con ese centro)
  const [utermVaoAllowed, setUtermVaoAllowed] = useState(false);
  const [showUtermDialog, setShowUtermDialog] = useState(false);
  const [utermDateIni, setUtermDateIni] = useState(yesterdayStr);
  const [utermDateFin, setUtermDateFin] = useState(yesterdayStr);
  const [utermDownloading, setUtermDownloading] = useState(false);
  const [shiftEditSaving, setShiftEditSaving] = useState(false);
  const [rectifyNewValue, setRectifyNewValue] = useState('');
  const [rectifyReason, setRectifyReason] = useState('');
  const [rectifyLoading, setRectifyLoading] = useState(false);

  useEffect(() => {
    apiFetch('/api/kpis/facets').then(r => r.json()).then(f => setFacets(f)).catch(() => {});
    // Detect stale data: if yesterday's sync failed, fall back to last
    // available date so the user sees historical fichajes instead of an
    // empty page.
    apiFetch('/api/kpis/last-available-date')
      .then(r => r.json())
      .then(info => {
        setLastDateInfo(info);
        if (info && info.data_is_stale && info.last_available_date) {
          setStaleInfo(info);
          setFechaIni(info.last_available_date);
          setFechaFin(info.last_available_date);
        }
      })
      .catch(() => {});
  }, [apiFetch]);

  // After every load, decide if we should show the "your centers have no data
  // for this date" banner (e.g. centro nuevo en RDS sin histórico aún).
  useEffect(() => {
    if (!lastDateInfo) return;
    if (loading) return;
    if (total > 0) {
      setUserEmptyInfo(null);
      return;
    }
    const userLast = lastDateInfo.user_last_available_date;
    const sel      = fechaFin || fechaIni;
    if (!userLast) {
      // User's centers have no data at all (centro nuevo, sin fichajes nunca)
      setUserEmptyInfo({
        user_last_available_date: null,
        user_kpis_on_last_day:    0,
        current_selected:         sel,
        no_data_at_all:           true,
      });
    } else if (sel && userLast !== sel) {
      // Selection has 0 results but their centers DO have data on another date
      setUserEmptyInfo({
        user_last_available_date: userLast,
        user_kpis_on_last_day:    lastDateInfo.user_kpis_on_last_day || 0,
        current_selected:         sel,
        no_data_at_all:           false,
      });
    } else {
      setUserEmptyInfo(null);
    }
  }, [total, loading, lastDateInfo, fechaIni, fechaFin]);

  const goToUserLastDay = () => {
    if (!userEmptyInfo) return;
    const d = userEmptyInfo.user_last_available_date;
    setFechaIni(d);
    setFechaFin(d);
    setUserEmptyInfo(null);
  };

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: perPage });
      if (fechaIni) params.append('fecha_ini', fechaIni);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      if (turnos.length)     params.append('turno',  turnos.join(','));
      if (estados.length)    params.append('estado', estados.join(','));
      if (centrosSel.length) params.append('centro', centrosSel.join(','));
      if (empleadosSel.length) params.append('empleado', empleadosSel.map(e => e.id).join(','));
      if (search) params.append('search', search);
      const res  = await apiFetch(`/api/kpis?${params}`);
      const json = await res.json();
      setData(json.data || []);
      setTotal(json.total || 0);
      setPages(json.pages || 1);
      setPage(p);
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
    setLoading(false);
  }, [apiFetch, fechaIni, fechaFin, turnos, estados, centrosSel, empleadosSel, search, perPage]);

  // ─── Detección cliente de "día sospechoso de sync parcial" (Phase 16) ─
  // Si el usuario está viendo un ÚNICO día y el ratio de turnos sin salida
  // supera el 40%, es muy probable que el sync capturase sólo las entradas
  // sin haber esperado a que llegasen las salidas del turno de tarde.
  const singleDaySelected = fechaIni && fechaFin && fechaIni === fechaFin;
  const singleDayStats = React.useMemo(() => {
    if (!singleDaySelected || total === 0) return null;
    const autoclose = data.filter(r => r.EstadoFichaje === 'Fichaje cerrado automáticamente').length;
    const ratio = data.length ? autoclose / data.length : 0;
    return { autoclose, ratio, sample: data.length };
  }, [data, total, singleDaySelected]);
  const showResyncBanner = singleDayStats && singleDayStats.ratio >= 0.40 && total >= 20;
  const [resyncing, setResyncing] = useState(false);
  const handleResyncDay = async () => {
    if (!fechaIni) return;
    if (!window.confirm(
      `¿Re-sincronizar el día ${fechaIni}?\n\n` +
      `Un ${Math.round((singleDayStats?.ratio ?? 0) * 100)}% de los fichajes visibles son "sin salida", lo que sugiere que la sync inicial se ejecutó antes de que RDS recibiera las salidas del turno de tarde.\n\n` +
      `El re-sync tarda entre 30 s y 2 min. Los KPIs se regenerarán con los datos actuales de RDS.`
    )) return;
    setResyncing(true);
    try {
      const res = await apiFetch('/api/sync/heal-gaps?days=90&detect_anomalies=true', { method: 'POST' });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json?.detail || `Error ${res.status}`);
        return;
      }
      if (json.nothing_to_do) {
        toast.info('El sistema no detecta anomalías en el rango. Datos consistentes.');
      } else {
        toast.success(`Re-sincronización iniciada en background (${json.missing_count} día(s)). Vuelve a cargar en 1-2 minutos.`);
      }
    } catch (e) {
      toast.error('Error al lanzar la re-sincronización');
    } finally {
      setResyncing(false);
    }
  };

  useEffect(() => { load(1); }, [fechaIni, fechaFin, turnos, estados, centrosSel, empleadosSel]);

  const handleSearch = (e) => { e.preventDefault(); load(1); };

  const openRectifyDialog = (row) => {
    setSelectedRow(row);
    setRectifyField('entrada');
    setRectifyNewValue('');
    setRectifyReason('');
    setShowRectifyDialog(true);
  };

  const handleRectifySubmit = async () => {
    if (!rectifyNewValue || !rectifyReason.trim()) {
      toast.error('Por favor, complete todos los campos');
      return;
    }

    setRectifyLoading(true);
    try {
      const oldValue = rectifyField === 'entrada' 
        ? selectedRow.first_ts_day 
        : selectedRow.last_ts_day;

      const response = await apiFetch('/api/rectifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kpi_id: selectedRow._id,
          field: rectifyField,
          old_value: oldValue,
          new_value: rectifyNewValue,
          reason: rectifyReason,
          id_employee: selectedRow.id_employee,
          svc_date: selectedRow.svc_date
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear rectificación');
      }

      toast.success('Rectificación guardada correctamente');
      setShowRectifyDialog(false);
      load(page); // Reload current page
    } catch (error) {
      toast.error(error.message || 'Error al guardar rectificación');
    } finally {
      setRectifyLoading(false);
    }
  };

  // ─── Shift override (corrección manual del turno) ──────────────────────
  const canEditShiftRow = (row) => {
    if (user?.role === 'admin') return true;
    const allowed = user?.allowed_centers || [];
    if (allowed.length === 0) return false;
    return allowed.includes(row?.centro_coste);
  };
  const openShiftEdit = (row) => {
    if (!canEditShiftRow(row)) {
      if (!user?.allowed_centers?.length) {
        toast.error('No tienes centros asignados. Contacta con un administrador para editar turnos.');
      } else {
        toast.error(`No tienes permisos sobre el centro "${row?.centro_coste || 'desconocido'}".`);
      }
      return;
    }
    setShiftEditRow(row);
    setShiftEditValue(row.shift_name || 'Mañana');
  };
  const saveShiftEdit = async () => {
    if (!shiftEditRow) return;
    setShiftEditSaving(true);
    try {
      const newVal = shiftEditValue === '__auto__' ? null : shiftEditValue;
      const res = await apiFetch(`/api/kpis/${shiftEditRow._id}/shift-override`, {
        method: 'PATCH',
        body: JSON.stringify({ shift_name: newVal }),
      });
      if (res.ok) {
        toast.success(newVal ? `Turno cambiado a ${newVal}` : 'Override eliminado (turno automático)');
        setShiftEditRow(null);
        load(page);
      } else {
        let detail = `Error ${res.status}`;
        try { const err = await res.json(); if (err?.detail) detail = err.detail; }
        catch (_parseErr) { /* response body not JSON — keep generic message */ }
        toast.error(detail);
      }
    } catch (e) {
      toast.error(e?.message === 'Sesión expirada'
        ? 'Tu sesión ha caducado. Vuelve a iniciar sesión.'
        : 'Error de conexión');
    }
    setShiftEditSaving(false);
  };

  // Build the shared query string used by: fetch list, XLSX export, IA DOCX report
  const buildFilterParams = () => {
    const p = new URLSearchParams();
    if (fechaIni) p.append('fecha_ini', fechaIni);
    if (fechaFin) p.append('fecha_fin', fechaFin);
    if (turnos.length)     p.append('turno',    turnos.join(','));
    if (estados.length)    p.append('estado',   estados.join(','));
    if (centrosSel.length) p.append('centro',   centrosSel.join(','));
    if (empleadosSel.length) p.append('empleado', empleadosSel.map(e => e.id).join(','));
    if (search) p.append('search', search);
    return p;
  };

  const handleExport = async () => {
    const params = buildFilterParams();
    const token = localStorage.getItem('smc_token');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/kpis.xlsx?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al exportar');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fichajes_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('XLSX exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar XLSX');
    }
  };

  const handleGenerateReport = async () => {
    const params = buildFilterParams();
    const token = localStorage.getItem('smc_token');
    toast.info('Generando reporte con IA... esto puede tomar unos segundos');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/docx?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al generar reporte');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_ia_${new Date().toISOString().slice(0,10)}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte IA generado correctamente');
    } catch (error) {
      toast.error('Error al generar reporte');
    }
  };

  // ─── UTE RM VAO — descarga específica ────────────────────────────────────
  // Comprobar permisos al montar y refresh cuando cambia el usuario
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/reports/uterm-vao/access');
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setUtermVaoAllowed(!!d?.allowed);
      } catch (_e) { /* silenciar */ }
    })();
    return () => { cancelled = true; };
  }, [apiFetch, user]);

  const handleUtermVaoExport = async () => {
    if (!utermDateIni || !utermDateFin) {
      toast.error('Selecciona un rango de fechas');
      return;
    }
    if (new Date(utermDateFin) < new Date(utermDateIni)) {
      toast.error('La fecha final no puede ser anterior a la inicial');
      return;
    }
    setUtermDownloading(true);
    const token = localStorage.getItem('smc_token');
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/uterm-vao/export?fecha_ini=${utermDateIni}&fecha_fin=${utermDateFin}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Error ${res.status}`;
        try { const err = await res.json(); if (err?.detail) msg = err.detail; }
        catch (_e) { /* keep generic */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `FichajesUTERMVAO_${utermDateIni}_a_${utermDateFin}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(dl);
      document.body.removeChild(a);
      toast.success('Export UTE RM VAO descargado');
      setShowUtermDialog(false);
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    } finally {
      setUtermDownloading(false);
    }
  };

  const clearFilters = () => {
    setSearch(''); setTurnos([]); setEstados([]); setCentrosSel([]); setEmpleadosSel([]);
    setFechaIni(yesterdayStr); setFechaFin(yesterdayStr);
  };

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('fichajes')}</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
            {total.toLocaleString('es-ES')} registros
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={handleExport}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', gap: '8px', display: 'flex', alignItems: 'center' }}
            size="sm"
            data-testid="export-xlsx-button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Exportar XLSX
          </Button>
          <Button
            onClick={handleGenerateReport}
            style={{ background: '#2563eb', border: '1px solid #1d4ed8', color: 'white', gap: '8px', display: 'flex', alignItems: 'center' }}
            size="sm"
            data-testid="generate-report-button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 18v-6"/><path d="M9 15l3-3 3 3"/></svg>
            Reporte IA
          </Button>
          {utermVaoAllowed && (
            <Button
              onClick={() => setShowUtermDialog(true)}
              style={{ background: '#7c3aed', border: '1px solid #6d28d9', color: 'white', gap: '8px', display: 'flex', alignItems: 'center' }}
              size="sm"
              data-testid="uterm-vao-export-button"
              title="Exportar el log crudo de fichajes de UTE RM VAO en el formato de RRHH"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/></svg>
              Export UTE RM VAO
            </Button>
          )}
        </div>
      </div>

      {/* Stale-data banner — shown when last sync failed and user is seeing
          historical data instead of yesterday's */}
      {staleInfo && (
        <div
          data-testid="stale-data-banner"
          style={{
            background: '#1f1a08',
            border: '1px solid #fbbf24',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fbbf24', fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>
              Datos no actualizados — última sincronización exitosa: {staleInfo.last_available_date}
            </div>
            <div style={{ color: '#cbd5e1', fontSize: '12px' }}>
              La sincronización de {staleInfo.yesterday} no se pudo completar (probablemente conexión RDS bloqueada).
              Mostrando los fichajes históricos disponibles. Los datos se actualizarán automáticamente al restablecer la conexión.
            </div>
          </div>
          <button
            onClick={() => setStaleInfo(null)}
            style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
            data-testid="stale-banner-close"
            aria-label="Cerrar aviso"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* User-RLS empty banner — shown when the current date has no data in
          the manager's centros, but a more recent date does. */}
      {userEmptyInfo && (
        <div
          data-testid="user-empty-banner"
          style={{
            background: userEmptyInfo.no_data_at_all ? '#1f1208' : '#0c2540',
            border: `1px solid ${userEmptyInfo.no_data_at_all ? '#fb923c' : '#3b82f6'}`,
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={userEmptyInfo.no_data_at_all ? "#fb923c" : "#60a5fa"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ color: userEmptyInfo.no_data_at_all ? '#fb923c' : '#60a5fa', fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>
              {userEmptyInfo.no_data_at_all
                ? 'Tus centros no tienen ningún fichaje registrado todavía'
                : 'Tus centros no tienen fichajes para esta fecha'}
            </div>
            <div style={{ color: '#cbd5e1', fontSize: '12px' }}>
              {userEmptyInfo.no_data_at_all ? (
                <>Es posible que se trate de un centro recién dado de alta o que aún no se haya empezado a fichar en RDS. Los datos aparecerán automáticamente cuando los empleados empiecen a fichar.</>
              ) : (
                <>Último día con datos en tus centros: <strong>{userEmptyInfo.user_last_available_date}</strong>
                  {userEmptyInfo.user_kpis_on_last_day > 0 && (
                    <> ({userEmptyInfo.user_kpis_on_last_day} {userEmptyInfo.user_kpis_on_last_day === 1 ? 'fichaje' : 'fichajes'})</>
                  )}
                </>
              )}
            </div>
          </div>
          {!userEmptyInfo.no_data_at_all && (
            <Button
              data-testid="user-empty-goto-btn"
              onClick={goToUserLastDay}
              style={{ background: '#3b82f6', color: 'white', fontWeight: 600, height: 32 }}
            >
              Ver esa fecha
            </Button>
          )}
          <button
            onClick={() => setUserEmptyInfo(null)}
            style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
            data-testid="user-empty-close"
            aria-label="Cerrar aviso"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          {/* Date range (calendar popovers) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ color: '#64748b', fontSize: '11px' }}>Rango de fechas</label>
            <DateRangePicker
              startDate={fechaIni}
              endDate={fechaFin}
              onChange={({ startDate, endDate }) => {
                if (startDate) setFechaIni(startDate);
                if (endDate)   setFechaFin(endDate);
              }}
              presets={true}
              testIdPrefix="fichajes"
            />
          </div>

          {/* Shift filter (multi-select) */}
          <div>
            <label style={{ color: '#64748b', fontSize: '11px', display: 'block', marginBottom: '4px' }}>{t('filtrarPorTurno')}</label>
            <MultiSelectAutocomplete
              options={facets.turnos}
              value={turnos}
              onChange={setTurnos}
              placeholder="Buscar turno…"
              testId="fichajes-filter-turno"
            />
          </div>

          {/* Status filter (multi-select) */}
          <div>
            <label style={{ color: '#64748b', fontSize: '11px', display: 'block', marginBottom: '4px' }}>{t('filtrarPorEstado')}</label>
            <MultiSelectAutocomplete
              options={facets.estados}
              value={estados}
              onChange={setEstados}
              placeholder="Buscar estado…"
              testId="fichajes-filter-estado"
            />
          </div>

          {/* Centro filter (multi-select autocomplete) */}
          <div>
            <label style={{ color: '#64748b', fontSize: '11px', display: 'block', marginBottom: '4px' }}>{t('filtrarPorCentro')}</label>
            <MultiSelectAutocomplete
              options={facets.centros}
              value={centrosSel}
              onChange={setCentrosSel}
              placeholder="Buscar centro…"
              testId="fichajes-filter-centro"
            />
          </div>

          {/* Employee filter (server-side autocomplete) */}
          <div>
            <label style={{ color: '#64748b', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Empleado</label>
            <EmployeeMultiSelect
              value={empleadosSel}
              onChange={setEmpleadosSel}
              token={localStorage.getItem('smc_token')}
              backendUrl={process.env.REACT_APP_BACKEND_URL}
              testId="fichajes-filter-empleado"
            />
          </div>

          {/* Search */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: '#64748b', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Buscar</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                data-testid="fichajes-filter-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('buscar')}
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
              />
              <Button type="submit" size="sm" style={{ background: '#2563eb', color: 'white', height: '36px', padding: '0 16px', whiteSpace: 'nowrap' }}>Buscar</Button>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            onClick={clearFilters}
            size="sm"
            style={{ color: '#64748b', height: '36px', alignSelf: 'flex-end' }}
          >
            {t('limpiarFiltros')}
          </Button>
        </form>
      </div>

      {/* Banner Phase 16: día sospechoso de sync parcial */}
      {showResyncBanner && (
        <div
          data-testid="fichajes-resync-banner"
          style={{
            background: 'rgba(251,146,60,0.10)',
            border: '1px solid rgba(251,146,60,0.4)',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ color: '#fb923c', flexShrink: 0, marginTop: 2 }}>⚠️</div>
          <div style={{ flex: 1, minWidth: 250, fontSize: 12.5, color: '#cbd5e1', lineHeight: 1.55 }}>
            <strong style={{ color: '#fdba74' }}>Este día parece tener datos parciales.</strong>{' '}
            El {Math.round(singleDayStats.ratio * 100)}% de los fichajes visibles son <em>“sin salida”</em> (categoría A), 
            un patrón habitual cuando la sincronización se ejecutó antes de que RDS recibiera las salidas del turno de tarde. 
            Puedes <strong>re-sincronizar el día</strong> para que se regeneren los KPIs con los datos actuales.
          </div>
          <Button
            onClick={handleResyncDay}
            disabled={resyncing}
            data-testid="fichajes-resync-btn"
            style={{
              background: '#c2410c',
              color: 'white',
              height: 36,
              padding: '0 16px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {resyncing ? '⏳ Lanzando...' : '🔄 Re-sincronizar día'}
          </Button>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #1e293b', borderTop: '3px solid #60a5fa', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} />
            {t('cargando')}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : data.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {t('sinResultados')}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="fichajes-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {[t('empleado'), t('fecha'), t('turno'), t('entrada'), t('salida'), t('horas'), t('fichajes_col'), t('estado'), t('dispositivo'), 'Acciones'].map(col => (
                    <th key={col} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Count total shifts per (id_employee, svc_date) to show "1/2" badges
                  const shiftCountMap = {};
                  data.forEach(r => {
                    const k = `${r.id_employee}__${r.svc_date}`;
                    shiftCountMap[k] = (shiftCountMap[k] || 0) + 1;
                  });
                  return data.map((row, i) => {
                  const nombre = (row.nombre || '').trim();
                  const apellidos = (row.apellidos || '').trim();
                  const fullName = [apellidos, nombre].filter(Boolean).join(', ');
                  const shiftKey = `${row.id_employee}__${row.svc_date}`;
                  const totalShifts = shiftCountMap[shiftKey] || 1;
                  const shiftIdx = typeof row.shift_idx === 'number' ? row.shift_idx : 0;
                  return (
                  <tr key={`${row.id_employee}-${row.svc_date}-${shiftIdx}-${i}`} className="table-row-hover" style={{ borderBottom: '1px solid #0f172a' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {fullName || <span style={{ color: '#64748b', fontStyle: 'italic' }}>Sin datos personales</span>}
                      </div>
                      <div style={{ color: '#475569', fontSize: '11px', fontFamily: 'IBM Plex Mono' }} data-testid="fichaje-employee-id">#{row.id_employee}</div>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '13px', fontFamily: 'IBM Plex Mono', whiteSpace: 'nowrap' }}>{formatDate(row.svc_date)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => openShiftEdit(row)}
                          data-testid={`shift-edit-${row._id}`}
                          title={
                            !canEditShiftRow(row)
                              ? (!user?.allowed_centers?.length
                                  ? 'Sin centros asignados — pide permisos a un administrador'
                                  : `Sin permisos sobre el centro "${row.centro_coste || ''}"`)
                              : (row.shift_name_manual
                                  ? `Override manual por ${row.shift_name_manual_by || 'admin'} — click para editar`
                                  : 'Click para corregir turno')
                          }
                          style={{ background: 'transparent', border: 'none', padding: 0, cursor: canEditShiftRow(row) ? 'pointer' : 'not-allowed', opacity: canEditShiftRow(row) ? 1 : 0.55 }}
                        >
                          <ShiftBadge shift={row.shift_name} />
                        </button>
                        {row.is_telework && (
                          <span
                            data-testid={`telework-badge-${row._id}`}
                            title={`Teletrabajo MyTime${row.telework_location ? ' · ' + row.telework_location : ''}`}
                            style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(168,85,247,0.18)', border: '1px solid rgba(168,85,247,0.45)', color: '#d8b4fe', fontWeight: 700, fontFamily: 'IBM Plex Mono', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            🏠 TELETRABAJO
                          </span>
                        )}
                        {row.shift_name_manual && (
                          <span
                            title={`Editado manualmente por ${row.shift_name_manual_by || 'admin'}`}
                            style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.45)', color: '#86efac', fontWeight: 700, fontFamily: 'IBM Plex Mono' }}
                          >
                            EDIT
                          </span>
                        )}
                        {totalShifts > 1 && (
                          <span
                            data-testid={`turno-indicator-${row.id_employee}-${shiftIdx}`}
                            title={`Turno ${shiftIdx + 1} de ${totalShifts} este día`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 7px',
                              borderRadius: '6px',
                              background: 'rgba(168,85,247,0.15)',
                              border: '1px solid rgba(168,85,247,0.45)',
                              color: '#c4b5fd',
                              fontSize: '10px',
                              fontWeight: 700,
                              fontFamily: 'IBM Plex Mono',
                              letterSpacing: '0.03em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            T{shiftIdx + 1}/{totalShifts}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#22c55e', fontSize: '13px', fontFamily: 'IBM Plex Mono', whiteSpace: 'nowrap' }}>{formatTime(row.first_ts_day)}</td>
                    <td style={{ padding: '10px 14px', color: row.fichaje_automatico ? '#f97316' : '#ef4444', fontSize: '13px', fontFamily: 'IBM Plex Mono', whiteSpace: 'nowrap' }}>
                      {formatTime(row.last_ts_day)}
                      {row.fichaje_automatico && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.7 }}>*</span>}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#f1f5f9', fontSize: '13px', fontFamily: 'IBM Plex Mono', whiteSpace: 'nowrap' }}>{row.day_hours}h</td>
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '13px', fontFamily: 'IBM Plex Mono', textAlign: 'center' }}>{row.num_fichajes}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge estado={row.EstadoFichaje} num_fichajes={row.num_fichajes} /></td>
                    <td
                      style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}
                      title={row.id_device_alias
                        ? `Serial: ${row.id_device}${row.id_device_location ? ' · ' + row.id_device_location : ''}`
                        : (row.id_device ? `Sin alias configurado` : '')}
                    >
                      {row.id_device_alias ? (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                          <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: 500 }}>{row.id_device_alias}</span>
                          <span style={{ color: '#475569', fontSize: '10px', fontFamily: 'IBM Plex Mono' }}>{row.id_device}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#475569', fontSize: '11px', fontFamily: 'IBM Plex Mono' }}>{row.id_device || '-'}</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRectifyDialog(row)}
                        data-testid="rectify-button"
                        style={{
                          background: '#0f172a',
                          border: '1px solid #334155',
                          color: '#60a5fa',
                          fontSize: '11px',
                          height: '28px',
                          padding: '0 10px',
                          marginRight: '6px'
                        }}
                      >
                        Rectificar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/empleados/${row.id_employee}/productividad`)}
                        data-testid={`productividad-${row.id_employee}`}
                        style={{
                          background: '#0f172a',
                          border: '1px solid #22c55e',
                          color: '#22c55e',
                          fontSize: '11px',
                          height: '28px',
                          padding: '0 10px'
                        }}
                        title="Ver productividad del empleado"
                      >
                        📊 Productividad
                      </Button>
                    </td>
                  </tr>
                  );
                });
                })()}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div data-testid="fichajes-pagination"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderTop: '1px solid #334155' }}>
            <span style={{ color: '#64748b', fontSize: '13px' }}>
              {t('pagina')} {page} {t('de')} {pages} &mdash; {total.toLocaleString('es-ES')} registros
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => load(page - 1)}
                style={{ background: '#0f172a', border: '1px solid #334155', color: page <= 1 ? '#334155' : '#94a3b8', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => load(page + 1)}
                style={{ background: '#0f172a', border: '1px solid #334155', color: page >= pages ? '#334155' : '#94a3b8', cursor: page >= pages ? 'not-allowed' : 'pointer' }}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Rectification Dialog */}
      <Dialog open={showRectifyDialog} onOpenChange={setShowRectifyDialog}>
        <DialogContent className="sm:max-w-[500px]" style={{ background: '#1e293b', border: '1px solid #334155' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600 }}>
              Rectificar Fichaje
            </DialogTitle>
          </DialogHeader>
          
          {selectedRow && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0' }}>
              {/* Employee Info */}
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
                <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                  {selectedRow.nombre ? `${selectedRow.apellidos}, ${selectedRow.nombre}` : selectedRow.id_employee}
                </div>
                <div style={{ color: '#64748b', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>
                  {formatDate(selectedRow.svc_date)} | {selectedRow.shift_name}
                </div>
              </div>

              {/* Current Values */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '10px', background: '#0f172a', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>Entrada actual</div>
                  <div style={{ color: '#22c55e', fontSize: '14px', fontFamily: 'IBM Plex Mono', fontWeight: 600 }}>
                    {formatTime(selectedRow.first_ts_day)}
                  </div>
                </div>
                <div style={{ padding: '10px', background: '#0f172a', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>Salida actual</div>
                  <div style={{ color: selectedRow.fichaje_automatico ? '#f97316' : '#ef4444', fontSize: '14px', fontFamily: 'IBM Plex Mono', fontWeight: 600 }}>
                    {formatTime(selectedRow.last_ts_day)}
                    {selectedRow.fichaje_automatico && <span style={{ marginLeft: '4px', fontSize: '10px' }}>*</span>}
                  </div>
                </div>
              </div>

              {/* Field to rectify */}
              <div>
                <Label htmlFor="rectify-field" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                  Campo a rectificar
                </Label>
                <Select value={rectifyField} onValueChange={setRectifyField}>
                  <SelectTrigger 
                    id="rectify-field"
                    style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '40px' }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1e293b', border: '1px solid #334155' }}>
                    <SelectItem value="entrada" style={{ color: '#f1f5f9' }}>Hora de Entrada</SelectItem>
                    <SelectItem value="salida" style={{ color: '#f1f5f9' }}>Hora de Salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* New value */}
              <div>
                <Label htmlFor="rectify-new-value" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                  Nuevo valor (formato HH:MM:SS)
                </Label>
                <Input
                  id="rectify-new-value"
                  type="text"
                  placeholder="08:00:00"
                  value={rectifyNewValue}
                  onChange={(e) => setRectifyNewValue(e.target.value)}
                  data-testid="rectify-new-value-input"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '40px' }}
                />
              </div>

              {/* Reason */}
              <div>
                <Label htmlFor="rectify-reason" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                  Motivo de la rectificación (obligatorio)
                </Label>
                <Textarea
                  id="rectify-reason"
                  placeholder="Ej: Empleado olvidó fichar salida. Confirmado por supervisor..."
                  value={rectifyReason}
                  onChange={(e) => setRectifyReason(e.target.value)}
                  rows={3}
                  data-testid="rectify-reason-input"
                  style={{ 
                    background: '#0f172a', 
                    border: '1px solid #334155', 
                    color: '#f1f5f9', 
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>

              {/* Warning */}
              <div style={{ 
                padding: '10px 12px', 
                background: '#78350f20', 
                border: '1px solid #78350f', 
                borderRadius: '6px',
                display: 'flex',
                gap: '8px'
              }}>
                <span style={{ color: '#fbbf24', fontSize: '14px' }}>⚠️</span>
                <span style={{ color: '#fcd34d', fontSize: '12px', lineHeight: '1.4' }}>
                  Esta acción quedará registrada en el sistema de auditoría y no se puede deshacer.
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowRectifyDialog(false)}
              disabled={rectifyLoading}
              style={{ color: '#64748b' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRectifySubmit}
              disabled={rectifyLoading}
              data-testid="rectify-submit-button"
              style={{
                background: '#2563eb',
                color: 'white',
                opacity: rectifyLoading ? 0.6 : 1
              }}
            >
              {rectifyLoading ? 'Guardando...' : 'Guardar Rectificación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SHIFT OVERRIDE MODAL */}
      {shiftEditRow && (
        <div
          data-testid="shift-edit-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
          onClick={() => !shiftEditSaving && setShiftEditRow(null)}
        >
          <Card onClick={e => e.stopPropagation()} style={{ background: '#1e293b', border: '1px solid #334155', padding: 24, maxWidth: 480, width: '100%' }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>Corregir turno</h3>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18 }}>
              {(shiftEditRow.apellidos || '') + ' ' + (shiftEditRow.nombre || '')} · {formatDate(shiftEditRow.svc_date)} · {formatTime(shiftEditRow.first_ts_day)} → {formatTime(shiftEditRow.last_ts_day)}
            </p>
            <Label style={{ color: '#94a3b8', fontSize: 12 }}>Turno asignado</Label>
            <select
              data-testid="shift-edit-select"
              value={shiftEditValue}
              onChange={e => setShiftEditValue(e.target.value)}
              style={{ width: '100%', marginTop: 6, background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
            >
              <option value="Mañana">Mañana</option>
              <option value="Tarde">Tarde</option>
              <option value="Noche">Noche</option>
              <option value="Partido">Partido</option>
              <option value="__auto__">— Volver a clasificación automática —</option>
            </select>
            {shiftEditRow.shift_name_manual && (
              <p style={{ fontSize: 11, color: '#86efac', marginTop: 8 }}>
                ⓘ Este fichaje tiene un override manual ({shiftEditRow.shift_name_manual}) por {shiftEditRow.shift_name_manual_by || 'admin'}.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShiftEditRow(null)} disabled={shiftEditSaving} data-testid="shift-edit-cancel">Cancelar</Button>
              <Button onClick={saveShiftEdit} disabled={shiftEditSaving} data-testid="shift-edit-save">
                {shiftEditSaving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* UTE RM VAO — Modal de descarga con rango de fechas */}
      {showUtermDialog && (
        <div
          data-testid="uterm-vao-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' }}
          onClick={() => !utermDownloading && setShowUtermDialog(false)}
        >
          <Card
            onClick={e => e.stopPropagation()}
            style={{ background: '#1e293b', border: '1px solid #7c3aed', padding: '28px', maxWidth: '460px', width: '100%' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ background: 'rgba(124,58,237,0.15)', borderRadius: 10, padding: '8px 10px', display: 'flex' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/></svg>
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Export UTE RM VAO</h2>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '2px 0 0' }}>Log crudo de fichajes en formato RRHH</p>
              </div>
            </div>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '8px 0 16px', lineHeight: 1.5 }}>
              Selecciona el rango de fechas. Se descargará un XLSX con una fila por cada fichaje (entrada, salida, intermedios),
              con las columnas: <span style={{ color: '#c4b5fd' }}>Nombre Completo · Division De Personal · NIF · Fecha · Hora</span>.
              Máximo 92 días por descarga.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '18px' }}>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Desde</Label>
                <input
                  type="date"
                  value={utermDateIni}
                  onChange={e => setUtermDateIni(e.target.value)}
                  data-testid="uterm-vao-fecha-ini"
                  style={{ width: '100%', marginTop: 4, background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '8px 10px', fontSize: '13px' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Hasta</Label>
                <input
                  type="date"
                  value={utermDateFin}
                  onChange={e => setUtermDateFin(e.target.value)}
                  data-testid="uterm-vao-fecha-fin"
                  style={{ width: '100%', marginTop: 4, background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '8px 10px', fontSize: '13px' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button
                variant="ghost"
                onClick={() => setShowUtermDialog(false)}
                disabled={utermDownloading}
                data-testid="uterm-vao-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUtermVaoExport}
                disabled={utermDownloading}
                data-testid="uterm-vao-download"
                style={{ background: '#7c3aed', color: 'white' }}
              >
                {utermDownloading ? 'Descargando…' : 'Descargar XLSX'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
